// ─────────────────────────────────────────────────────────────────
// Glass-i — Manual Charge Master page (Stage 6Q).
//
// Maintainable catalogue of document-level "additional cost" charges
// (name + default Rs). Picked on the Proforma manual-charges section.
// Modal-based CRUD (AntD), mirroring the Other Charges master convention.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Button, Input, InputNumber, Modal, Form, Switch, Select, Tag,
  Space, Tooltip, Popconfirm, message
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';

import { manualChargeApi } from './manualChargeApi';
import { loadOtherChargesDropdownsSafe } from '../../otherCharges/otherChargesApi';

// Statutory GST slabs (mirrors backend otherCharges.constants GST_RATE_SLABS) —
// the resilient fallback when the tax-master dropdown is unavailable.
const GST_SLAB_FALLBACK = [0, 0.25, 3, 5, 12, 18, 28].map((v) => ({ value: v, label: `${v}%` }));

function apiError(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message)
    || (e && e.message) || fallback;
}

export default function ManualChargeMasterPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // row being edited, or null for create
  const [saving, setSaving] = useState(false);
  const [gstRates, setGstRates] = useState(GST_SLAB_FALLBACK);
  const [form] = Form.useForm();

  // GST % options bound from the tax-master (same source the Other Charges master
  // uses); falls back to the statutory slabs when the dropdown is unavailable.
  useEffect(() => {
    let alive = true;
    loadOtherChargesDropdownsSafe().then((d) => {
      if (!alive) return;
      const list = Array.isArray(d && d.gstRates) && d.gstRates.length ? d.gstRates : GST_SLAB_FALLBACK;
      setGstRates(list);
    });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await manualChargeApi.list({ limit: 500 });
      setRows((out && out.rows) || []);
    } catch (e) {
      message.error(apiError(e, 'Failed to load manual charges.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.chargeName, r.remarks].filter(Boolean)
      .some((f) => String(f).toLowerCase().includes(q)));
  }, [rows, search]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, defaultRate: 0, gstRate: 0 });
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback((row) => {
    setEditing(row);
    form.setFieldsValue({
      chargeName: row.chargeName, defaultRate: row.defaultRate, gstRate: row.gstRate,
      isActive: row.isActive, remarks: row.remarks || ''
    });
    setModalOpen(true);
  }, [form]);

  const submit = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); } catch (_e) { return; }
    setSaving(true);
    try {
      const payload = {
        chargeName: values.chargeName,
        defaultRate: values.defaultRate != null ? Number(values.defaultRate) : 0,
        gstRate: values.gstRate != null ? Number(values.gstRate) : 0,
        isActive: values.isActive !== false,
        remarks: values.remarks || null
      };
      if (editing) await manualChargeApi.update(editing.id, payload);
      else await manualChargeApi.create(payload);
      message.success(editing ? 'Manual charge updated.' : 'Manual charge added.');
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(apiError(e, 'Failed to save manual charge.'));
    } finally {
      setSaving(false);
    }
  }, [form, editing, load]);

  const remove = useCallback(async (row) => {
    try {
      await manualChargeApi.remove(row.id);
      message.success('Manual charge removed.');
      await load();
    } catch (e) {
      message.error(apiError(e, 'Failed to remove manual charge.'));
    }
  }, [load]);

  const columns = [
    { title: 'S No', dataIndex: 'sno', width: 70, align: 'right' },
    { title: 'Charge Name', dataIndex: 'chargeName', ellipsis: true,
      render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: 'Default Rs', dataIndex: 'defaultRate', width: 110, align: 'right',
      render: (v) => Number(v || 0).toFixed(2) },
    { title: 'GST %', dataIndex: 'gstRate', width: 80, align: 'right',
      render: (v) => `${Number(v || 0).toFixed(2)}%` },
    { title: 'Active', dataIndex: 'isActive', width: 90, align: 'center',
      render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>) },
    { title: 'Remarks', dataIndex: 'remarks', ellipsis: true, render: (v) => v || <span className="text-slate-300">—</span> },
    {
      title: '', key: 'actions', width: 92, align: 'center',
      render: (_v, row) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Popconfirm title="Remove this manual charge?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(row)}>
            <Tooltip title="Remove"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="p-4">
      <Card
        title={<span className="text-[15px] font-bold text-slate-800">Manual Charge Master</span>}
        extra={(
          <Space>
            <Input allowClear size="small" prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Search name / remarks" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} />
            <Button size="small" icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Charge</Button>
          </Space>
        )}
      >
        <div className="text-[12px] text-slate-500 mb-2">
          Catalogue of document-level additional costs. On a Proforma you pick an active charge here; Amount = Area × Rs, taxed at the GST % set below.
        </div>
        <Table
          size="small" rowKey="id" loading={loading} columns={columns} dataSource={filtered}
          pagination={{ pageSize: 15, size: 'small', showSizeChanger: false, showTotal: (t) => `${t} charge${t === 1 ? '' : 's'}` }}
        />
      </Card>

      <Modal
        open={modalOpen} onCancel={() => setModalOpen(false)} onOk={submit} confirmLoading={saving}
        title={editing ? 'Edit Manual Charge' : 'Add Manual Charge'}
        okText={editing ? 'Save' : 'Add'} destroyOnClose width={460}
      >
        <Form form={form} layout="vertical" requiredMark="optional" className="mt-2">
          <Form.Item name="chargeName" label="Charge Name"
            rules={[{ required: true, message: 'Charge name is required.' }, { max: 120, message: 'Max 120 characters.' }]}>
            <Input placeholder="e.g. Site measurement, Special courier, Crane hire" autoFocus />
          </Form.Item>
          <Form.Item name="defaultRate" label="Default Rs (rate / sqm)"
            tooltip="Pre-fills the Rs column on the Proforma when this charge is picked. Editable per document.">
            <InputNumber min={0} controls={false} prefix="₹" style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
          <Form.Item name="gstRate" label="GST %"
            tooltip="GST rate applied to this charge on the Proforma (CGST+SGST or IGST per supply type). Bound to the tax-master GST slabs.">
            <Select options={gstRates} placeholder="Select GST %" optionFilterProp="label" showSearch />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} maxLength={2000} placeholder="Optional note" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
